import { Form } from 'antd';
import { OrderType } from 'typings';
import { useCallback, useState } from 'react';
import { useOrderListQuery } from 'api/orders';
import { useNavigate } from 'react-router-dom';
import { ADMIN_ROUTES } from 'constants/routes';
import { dateFormatter } from 'constants/index';
import { OrderFilterParams, OrderListArgument } from 'models/Order';

// Simple debounce utility
const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const useOrders = (forDashboard?: boolean) => {
  const navigate = useNavigate();
  // const [searchParams, setSearchParams] = useSearchParams();
  const [form] = Form.useForm();

  // Initialize pagination from URL params or defaults
  const [pagination, setPagination] = useState({
    page: 1,
    size: 10,
  });

  const [sorting, setSorting] = useState<Record<string, any>>({
    sortField: undefined,
    sortOrder: undefined,
  });

  const [search, setSearch] = useState('');
  // const [statusFilter, setStatusFilter] = useState<OrderStatusType | 'ALL'>('ALL');
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<OrderFilterParams>({});
  const [addModalState, setAddModalState] = useState<boolean>(false);

  // Prepare query parameters
  const queryParams: OrderListArgument = {
    page: pagination.page,
    size: pagination.size,
    search: search || undefined,
    sortField: sorting.sortField,
    sortOrder: sorting.sortOrder,
    filters: {
      ...filters,
      // ...(statusFilter !== 'ALL' && { status: [statusFilter] }),
    },
    forDashboard,
  };

  const { data: orderData, isLoading } = useOrderListQuery(queryParams);

  const data = orderData?.data?.content || [];
  const totalElements = orderData?.data?.totalElements || 0;

  // Calculate status counts for filter buttons (from all data, not filtered)
  // const allOrdersQuery = useOrderListQuery({
  //   page: 1,
  //   size: 1000, // Get a large number to get all orders for counting
  //   filters: filters, // Keep other filters but not status filter
  //   forDashboard,
  // });

  // const statusCounts = useMemo(() => {
  //   const allOrders = allOrdersQuery.data?.data?.content || [];
  //   const counts: Record<string, number> = {};
  //
  //   allOrders.forEach((order: any) => {
  //     const status = order.status;
  //     counts[status] = (counts[status] || 0) + 1;
  //   });
  //
  //   return counts;
  // }, [allOrdersQuery.data]);

  // Update URL params when pagination, sorting, or search changes
  // useEffect(() => {
  //   const newParams = new URLSearchParams();
  //
  //   if (pagination.page > 1) newParams.set('page', pagination.page.toString());
  //   if (pagination.size !== 10) newParams.set('size', pagination.size.toString());
  //   if (sorting.sortField) newParams.set('sortField', sorting.sortField);
  //   if (sorting.sortOrder) newParams.set('sortOrder', sorting.sortOrder);
  //   if (search) newParams.set('search', search);
  //
  //   setSearchParams(newParams);
  // }, [pagination, sorting, search, setSearchParams]);

  const routeChange = (id: string) => {
    navigate(`${ADMIN_ROUTES.orders.view}/${id}/${ADMIN_ROUTES.orders.general}`);
  };

  const handleTableChange = (paginationConfig: any, filters: any, sorter: any) => {
    // Handle pagination
    setPagination({
      page: paginationConfig.current,
      size: paginationConfig.pageSize,
    });

    const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
    const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
    const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
    setSorting({
      sortField: sortField as string,
      sortOrder: order as OrderType,
    });
  };

  const debouncedSearch = debounce((value: string) => {
    setSearch(value);
    setPagination({ ...pagination, page: 1 }); // Reset to first page on search
  }, 300);

  const onSearch = (value: string) => {
    debouncedSearch(value);
  };

  const openDrawer = () => {
    setFilterDrawerVisible(true);
  };

  const closeDrawer = useCallback(() => {
    setFilterDrawerVisible(false);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.clientId && { clientId: values.clientId }),
        ...(values.countryId && { countryId: values.countryId }),
        ...(values.assignedRoleId && { assignedRoleId: values.assignedRoleId }),
        ...(values.minPriceRange && { minPriceRange: values.minPriceRange }),
        ...(values.maxPriceRange && { maxPriceRange: values.maxPriceRange }),
        ...(values.minClientConversionRate && { minClientConversionRate: values.minClientConversionRate }),
        ...(values.maxClientConversionRate && { maxClientConversionRate: values.maxClientConversionRate }),
        ...(values.minBrandConversionRate && { minBrandConversionRate: values.minBrandConversionRate }),
        ...(values.maxBrandConversionRate && { maxBrandConversionRate: values.maxBrandConversionRate }),
        ...(values.clientTypeId && { clientTypeId: values.clientTypeId }),
        ...(values.brandTags?.length && { brandTags: values.brandTags }),
        ...(values.clientTags?.length && { clientTags: values.clientTags }),
        ...(values.region?.length && { region: values.region }),
        ...(values.status && { status: [values.status] }), // Convert single status to array
        ...(values.createdDate?.[0] && {
          createdDateFrom: dateFormatter(values.createdDate[0], 'start'),
        }),
        ...(values.createdDate?.[1] && {
          createdDateTo: dateFormatter(values.createdDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
      };

      setFilters(_filters);

      // Sync status filter with drawer selection
      // if (values.status) {
      //   setStatusFilter(values.status as OrderStatusType);
      // }
      // else {
      // No status selected, set to 'ALL'
      // setStatusFilter('ALL');
      // }

      setPagination({ ...pagination, page: 1 }); // Reset to first page on filter
      closeDrawer();
    },
    [pagination, closeDrawer]
  );

  const resetFilters = () => {
    setFilters({});
    setPagination({ ...pagination, page: 1 }); // Reset to first page on filter reset
  };

  const onAddModalStateChange = useCallback(() => {
    setAddModalState((prevState) => !prevState);
  }, []);

  // const onStatusFilterChange = (status: OrderStatusType | 'ALL') => {
  //   setStatusFilter(status);
  //
  //   // Update form field to sync with drawer
  //   if (status !== 'ALL') {
  //     form.setFieldValue('status', status);
  //     // Also update the filters state to include the status as array
  //     setFilters((prev) => ({ ...prev, status: [status] }));
  //   } else {
  //     form.setFieldValue('status', undefined);
  //     // Remove status from filters
  //     const { status: _, ...restFilters } = filters;
  //     setFilters(restFilters);
  //   }
  //
  //   setPagination({ ...pagination, page: 1 }); // Reset to first page on status filter
  // };

  return {
    data,
    isLoading,
    routeChange,
    pagination,
    onSearch,
    closeDrawer,
    openDrawer,
    filterDrawerVisible,
    form,
    handleTableChange,
    applyFilters,
    resetFilters,
    totalElements,
    onAddModalStateChange,
    addModalState,
    // statusFilter,
    // onStatusFilterChange,
    // statusCounts,
  };
};
